import { useMemo, useState, useCallback } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { MEASURES_LABELS } from "../../libs/measures/constants";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const LINE_COLORS = [
  "#06B6D4", "#F59E0B", "#EF4444", "#22C55E", "#A855F7",
  "#EC4899", "#3B82F6", "#F97316", "#14B8A6", "#8B5CF6",
];

type TimeFilter = "all" | "year" | "6m" | "4m" | "3m" | "2m" | "month" | "week";

type MeasureChartProps = {
  dates: Set<string>;
  historyList: Record<string, any>;
  measureKeys: string[];
  unit: string;
  title: string;
  showWeeklyAverage?: boolean;
  storageKey?: string;
};

const formatLabel = (date: string) =>
  date.split("-")[2] + "/" + date.split("-")[1];

const formatLabelFull = (date: string) =>
  date.split("-")[2] + "/" + date.split("-")[1] + "/" + date.split("-")[0];

const TIME_FILTER_OPTIONS: [TimeFilter, string][] = [
  ["all", "Tutto"],
  ["year", "Anno"],
  ["6m", "6M"],
  ["4m", "4M"],
  ["3m", "3M"],
  ["2m", "2M"],
  ["month", "Mese"],
  ["week", "Sett."],
];

const getFilteredDates = (dates: string[], filter: TimeFilter): string[] => {
  if (filter === "all") return dates;

  const now = new Date();
  const cutoff = new Date();

  switch (filter) {
    case "year":
      cutoff.setFullYear(now.getFullYear() - 1);
      break;
    case "6m":
      cutoff.setMonth(now.getMonth() - 6);
      break;
    case "4m":
      cutoff.setMonth(now.getMonth() - 4);
      break;
    case "3m":
      cutoff.setMonth(now.getMonth() - 3);
      break;
    case "2m":
      cutoff.setMonth(now.getMonth() - 2);
      break;
    case "month":
      cutoff.setMonth(now.getMonth() - 1);
      break;
    case "week":
      cutoff.setDate(now.getDate() - 7);
      break;
  }

  return dates.filter((d) => new Date(d) >= cutoff);
};

const WEEKLY_AVG_KEY = "__weeklyAverage__";

// Plugin Chart.js: disegna bande anno nella parte superiore del grafico
const yearBandsPlugin = {
  id: "yearBands",
  beforeDraw(chart: any) {
    const yearRanges: { year: string; startIdx: number; endIdx: number }[] =
      chart.options._yearRanges;
    if (!yearRanges || yearRanges.length <= 1) return;

    const { ctx } = chart;
    const xScale = chart.scales.x;
    const chartArea = chart.chartArea;
    const bandHeight = 20;
    const isDark = document.documentElement.classList.contains("dark");
    const colors = isDark
      ? ["rgba(59, 130, 246, 0.08)", "rgba(139, 92, 246, 0.08)"]
      : ["rgba(59, 130, 246, 0.06)", "rgba(139, 92, 246, 0.06)"];
    const textColor = isDark ? "rgba(156, 163, 175, 0.7)" : "rgba(107, 114, 128, 0.7)";

    ctx.save();
    yearRanges.forEach((range, idx) => {
      const xStart = xScale.getPixelForValue(range.startIdx);
      const xEnd = xScale.getPixelForValue(range.endIdx);
      const width = xEnd - xStart;

      // Banda colorata in alto
      ctx.fillStyle = colors[idx % colors.length];
      ctx.fillRect(xStart, chartArea.top, width, bandHeight);

      // Linea separatore verticale (tranne il primo)
      if (idx > 0) {
        ctx.strokeStyle = isDark ? "rgba(107, 114, 128, 0.3)" : "rgba(156, 163, 175, 0.3)";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(xStart, chartArea.top);
        ctx.lineTo(xStart, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Label anno centrata nella banda
      ctx.fillStyle = textColor;
      ctx.font = "600 11px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(range.year, xStart + width / 2, chartArea.top + bandHeight / 2);
    });
    ctx.restore();
  },
};

export const MeasureChart: React.FC<MeasureChartProps> = ({
  dates,
  historyList,
  measureKeys,
  unit,
  title,
  showWeeklyAverage = false,
  storageKey,
}) => {
  const [timeFilter, setTimeFilter] = usePersistedState<TimeFilter>(storageKey || `fitshape:chart:timeFilter:${title}`, "all");
  const hasLegend = measureKeys.length > 1;

  const isDark = document.documentElement.classList.contains("dark");
  const gridColor = isDark ? "#374151" : "#e5e7eb";
  const tickColor = isDark ? "#9ca3af" : "#9ca3af";

  // Set di dataset nascosti (per toggle legenda)
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  const toggleKey = useCallback((key: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const resetLegend = useCallback(() => {
    const allKeys = [...measureKeys];
    if (showWeeklyAverage) allKeys.push(WEEKLY_AVG_KEY);
    setHiddenKeys(new Set(allKeys));
  }, [measureKeys, showWeeklyAverage]);

  const showAllLegend = useCallback(() => {
    setHiddenKeys(new Set());
  }, []);

  const sortedDates = useMemo(
    () => Array.from(dates).sort(),
    [dates]
  );

  const filteredDates = useMemo(
    () => getFilteredDates(sortedDates, timeFilter),
    [sortedDates, timeFilter]
  );

  const datasets = useMemo(() =>
    measureKeys.map((key, idx) => {
      const color = LINE_COLORS[idx % LINE_COLORS.length];
      const label = MEASURES_LABELS[key as keyof typeof MEASURES_LABELS] || key;
      return {
        key,
        label,
        color,
        data: filteredDates.map((date) => {
          const val = historyList[date]?.[key];
          return val != null ? parseFloat(val) : null;
        }),
      };
    }),
  [filteredDates, historyList, measureKeys]);

  // Media mobile 7 giorni: per ogni punto, media di tutti i valori nei 7 giorni precedenti (per data reale)
  const weeklyAverageData = useMemo(() => {
    if (!showWeeklyAverage || datasets.length !== 1) return null;
    const values = datasets[0].data;

    return filteredDates.map((dateStr, i) => {
      const currentDate = new Date(dateStr).getTime();
      const sevenDaysAgo = currentDate - 7 * 86400000;
      const window: number[] = [];

      for (let j = 0; j <= i; j++) {
        const d = new Date(filteredDates[j]).getTime();
        if (d > sevenDaysAgo && d <= currentDate && values[j] != null) {
          window.push(values[j] as number);
        }
      }
      if (window.length < 2) return null; // almeno 2 misurazioni per mostrare la media
      return Math.round((window.reduce((s, v) => s + v, 0) / window.length) * 100) / 100;
    });
  }, [showWeeklyAverage, datasets, filteredDates]);

  // Calcola i range anno per le bande
  const yearRanges = useMemo(() => {
    if (filteredDates.length === 0) return [];
    const ranges: { year: string; startIdx: number; endIdx: number }[] = [];
    let currentYear = filteredDates[0].split("-")[0];
    let startIdx = 0;
    for (let i = 1; i < filteredDates.length; i++) {
      const year = filteredDates[i].split("-")[0];
      if (year !== currentYear) {
        ranges.push({ year: currentYear, startIdx, endIdx: i - 1 });
        currentYear = year;
        startIdx = i;
      }
    }
    ranges.push({ year: currentYear, startIdx, endIdx: filteredDates.length - 1 });
    return ranges;
  }, [filteredDates]);

  const hasMultipleYears = yearRanges.length > 1;

  const chartData = useMemo(() => {
    const ds = datasets.map(({ key, label, color, data }) => ({
      label,
      data,
      borderColor: color,
      backgroundColor: color,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointHitRadius: 15,
      borderWidth: 2,
      fill: false,
      tension: 0.2,
      spanGaps: true,
      hidden: hiddenKeys.has(key),
    }));

    if (weeklyAverageData) {
      ds.push({
        label: "Media mobile 7gg",
        data: weeklyAverageData,
        borderColor: "#EF4444",
        backgroundColor: "#EF4444",
        pointBackgroundColor: "transparent",
        pointBorderColor: "transparent",
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 10,
        borderWidth: 2.5,
        fill: false,
        tension: 0.4,
        spanGaps: true,
        hidden: hiddenKeys.has(WEEKLY_AVG_KEY),
      });
    }

    return {
      labels: filteredDates.map((d) =>
        filteredDates.length > 15 ? formatLabel(d) : formatLabelFull(d)
      ),
      datasets: ds,
    };
  }, [filteredDates, datasets, weeklyAverageData, hiddenKeys]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: hasMultipleYears ? 22 : 0 },
    },
    interaction: {
      mode: "nearest" as const,
      intersect: false,
      axis: "xy" as const,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (item: any) => {
            const val = item.raw;
            if (val === null) return `${item.dataset.label}: -`;
            return `${item.dataset.label}: ${val} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: true, color: gridColor },
        ticks: { color: tickColor, font: { size: 10 }, maxRotation: 45 },
        border: { color: gridColor },
      },
      y: {
        grid: { display: true, color: gridColor },
        ticks: {
          color: tickColor,
          font: { size: 11 },
          callback: (value: any) => `${value} ${unit}`,
        },
        border: { color: gridColor },
      },
    },
    _yearRanges: hasMultipleYears ? yearRanges : undefined,
  } as any), [unit, gridColor, tickColor, hasMultipleYears, yearRanges]);

  const allHidden = hiddenKeys.size >= measureKeys.length + (showWeeklyAverage ? 1 : 0);

  const timeFilterButtons = (
    <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
      {TIME_FILTER_OPTIONS.map(([value, label]) => (
        <button
          key={value}
          onClick={() => setTimeFilter(value)}
          className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
            timeFilter === value
              ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (filteredDates.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h4>
          {timeFilterButtons}
        </div>
        <p className="text-xs text-gray-400 text-center py-8">Nessun dato per il periodo selezionato</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h4>
        {timeFilterButtons}
      </div>
      <div className="flex gap-4 items-start">
        {/* Legenda interattiva a sinistra */}
        {(hasLegend || showWeeklyAverage) && (
          <div className="flex flex-col gap-1 justify-center flex-shrink-0 pr-4 border-r border-gray-200 dark:border-gray-700 w-[160px]">
            {datasets.map(({ key, label, color }) => {
              const isHidden = hiddenKeys.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleKey(key)}
                  className={`flex items-center gap-1.5 text-[11px] py-0.5 text-left transition-opacity ${
                    isHidden
                      ? "opacity-35"
                      : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className={`break-words ${isHidden ? "line-through" : ""}`}>{label}</span>
                </button>
              );
            })}
            {showWeeklyAverage && (
              <button
                type="button"
                onClick={() => toggleKey(WEEKLY_AVG_KEY)}
                className={`flex items-center gap-1.5 text-[11px] py-0.5 text-left mt-1 pt-1 border-t border-gray-100 dark:border-gray-700 transition-opacity ${
                  hiddenKeys.has(WEEKLY_AVG_KEY)
                    ? "opacity-35"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: "#EF4444" }}
                />
                <span className={`break-words ${hiddenKeys.has(WEEKLY_AVG_KEY) ? "line-through" : ""}`}>Media mobile 7gg</span>
              </button>
            )}
            {/* Reset / Mostra tutti */}
            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              {!allHidden ? (
                <button
                  type="button"
                  onClick={resetLegend}
                  className="text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  Nascondi tutte
                </button>
              ) : (
                <button
                  type="button"
                  onClick={showAllLegend}
                  className="text-[10px] text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  Mostra tutte
                </button>
              )}
            </div>
          </div>
        )}
        {/* Grafico */}
        <div className="flex-1 min-w-0" style={{ height: 280 }}>
          <Line data={chartData} options={options} plugins={[yearBandsPlugin]} />
        </div>
      </div>
    </div>
  );
};
